/**
 * useProfileStatus Hook - Member Profile Status Management - By John Bellew - Infinia.ie - last updated 01/07/2025
 * 
 * Custom React hook for managing member profile completion status and disclaimer acceptance.
 * Provides comprehensive profile validation, warning management, and automatic status tracking
 * based on required fields and disclaimer acceptance. Integrates with Auth0 authentication
 * and handles profile updates with real-time status monitoring.
 * 
 * @fileoverview Custom hook for profile status management and validation
 */

import { useState, useEffect, useContext } from 'react';
import apiHelper from '../utils/apiHelper';
import { AuthContext } from '../components/Auth/AuthContext';

/**
 * Custom hook for managing member profile status and validation
 * 
 * Monitors profile completion status based on required fields (jobTitle, company,
 * memberCohort, NoOfYrsintheIndustry) and disclaimer acceptance. Provides automatic
 * status updates, warning dismissal functionality, and comprehensive profile state
 * management for the entire application.
 * 
 * @returns {Object} Hook return object with profile status and management functions
 * @returns {string|null} returns.profileStatus - Current profile status ('incomplete', 'complete', 'error', or null)
 * @returns {boolean} returns.loading - Loading state indicator for profile checks
 * @returns {Object|null} returns.profile - Complete profile data object from API
 * @returns {Function} returns.dismissWarning - Function to dismiss profile warning
 * @returns {boolean} returns.showWarning - Computed property indicating if warning should show
 * 
 * @example
 * // Basic usage for profile status checking
 * const { profileStatus, loading, showWarning } = useProfileStatus();
 * 
 * @example
 * // Advanced usage with warning dismissal
 * const { profileStatus, profile, dismissWarning, showWarning } = useProfileStatus();
 * if (showWarning) {
 *   return <ProfileWarning onDismiss={dismissWarning} />;
 * }
 * 
 * @example
 * // Profile data access with loading states
 * const { profile, loading, profileStatus } = useProfileStatus();
 * if (loading) return <Spinner />;
 * if (profileStatus === 'error') return <ErrorMessage />;
 */
export const useProfileStatus = () => {
  const { user: [user], loading: [authLoading] } = useContext(AuthContext);
  const isAuthenticated = !!user;
  const [profileStatus, setProfileStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  // Profile status checking effect
  useEffect(() => {
    /**
     * Checks profile completion status and disclaimer acceptance
     * Validates required fields and sets appropriate status based on completeness
     */
    const checkProfile = async () => {
      if (isAuthenticated && user?.profile?.email) {
        try {
          const profileData = await apiHelper.getMemberProfileByEmail(user.profile.email);
          
          setProfile(profileData);
          
          // Check for required fields and disclaimer acceptance
          const isIncomplete = !profileData.jobTitle || 
                               !profileData.company || 
                               !profileData.memberCohort || 
                               !profileData.NoOfYrsintheIndustry;
          const disclaimerAccepted = profileData.fieldVisibility?.disclaimerAccepted === true;
          
          // Show disclaimer if profile incomplete OR disclaimer not accepted
          if (isIncomplete || !disclaimerAccepted) {
            setProfileStatus('incomplete');
          } else {
            setProfileStatus('complete');
          }
        } catch (error) {
          // Handle Auth0 not ready gracefully
          if (error.message === 'AUTH0_NOT_READY') {
            // Keep loading state, don't set error
            return;
          }
          setProfileStatus('error');
        }
      }
      setLoading(false);
    };

    if (!authLoading) {
      checkProfile();
    }
  }, [user, isAuthenticated, authLoading]);

  /**
   * Dismisses profile warning by updating profile warning status
   * Sets profile status to complete after successful API update
   */
  const dismissWarning = async () => {
    try {
      await apiHelper.updateProfileWarning({
        email: user.profile.email
      });
      setProfileStatus('complete');
    } catch (error) {
      // Error updating profile visibility - handled gracefully
    }
  };

  return {
    profileStatus,
    loading,
    profile,
    dismissWarning,
    showWarning: profileStatus === 'incomplete'
  };
}; 